import {
  Add as AddIcon,
  Check as CheckIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNotification } from "../../../contexts/NotificationContext";
import { researchApi } from "../../../services/researchApi";
import { searchOrganizations } from "../../../services/searchApi";

interface OrgSearchResult {
  id: string;
  name: string;
  home_country?: string | null;
}

interface AddCandidatesModalProps {
  open: boolean;
  onClose: () => void;
  trackId: string;
  /** Stage the user is adding from; stamped onto the new candidates. */
  initialDecisionState: "candidate" | "shortlisted";
  /** Called on close when at least one candidate was added (parent refetches). */
  onCandidatesAdded: () => void;
}

/**
 * "Add Candidates" dialog for the discovery and evaluation views.
 *
 * Two ways in: search the knowledge graph (name + country, click to add), or
 * paste newline-separated domains — domains already carried by a graph
 * organization link to it, unknown ones become pending candidates whose
 * website profile is crawled and whose graph ingestion is queued.
 */
const AddCandidatesModal: React.FC<AddCandidatesModalProps> = ({
  open,
  onClose,
  trackId,
  initialDecisionState,
  onCandidatesAdded,
}) => {
  const { showNotification } = useNotification();
  const [tab, setTab] = useState<0 | 1>(0);

  // --- Search tab state ---
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<OrgSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const [addingId, setAddingId] = useState<string | null>(null);

  // --- Domain tab state ---
  const [domainsText, setDomainsText] = useState("");
  const [domainsFocused, setDomainsFocused] = useState(false);
  const [submittingDomains, setSubmittingDomains] = useState(false);


  // Debounced graph search.
  useEffect(() => {
    if (!open || tab !== 0) return;
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await searchOrganizations({ q: term, limit: 20 });
        setSearchResults(
          (response.results || []).map((row: any) => ({
            id: row.id,
            name: row.name,
            home_country: row.home_country ?? row.country_iso2 ?? null,
          })),
        );
      } catch {
        showNotification("Graph search failed", "error");
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [searchTerm, open, tab, showNotification]);

  const handleAddOrg = async (org: OrgSearchResult) => {
    setAddingId(org.id);
    try {
      const response = await researchApi.addEntityToTrack({
        track: trackId,
        kg_entity_id: org.id,
        kg_entity_type: "organization",
        reason: "Added via candidate search",
        initial_decision_state: initialDecisionState,
      });
      setAddedIds((previous) => new Set(previous).add(org.id));
      if (response.status === "created") {
        // Refresh the list behind the modal right away — waiting for the
        // idle poll makes the add look like it didn't work.
        onCandidatesAdded();
      } else {
        showNotification(`${org.name} is already in this track`, "info");
      }
    } catch {
      showNotification(`Failed to add ${org.name}`, "error");
    } finally {
      setAddingId(null);
    }
  };

  const handleSubmitDomains = async () => {
    const domains = domainsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    if (domains.length === 0) return;
    setSubmittingDomains(true);
    try {
      const response = await researchApi.addCandidatesByDomains({
        track: trackId,
        domains,
        reason: "Added via add-by-domain",
        initial_decision_state: initialDecisionState,
      });
      const counts = {
        linked: 0,
        created: 0,
        existing: 0,
        invalid: [] as string[],
      };
      for (const result of response.results) {
        if (result.status === "linked_existing") counts.linked += 1;
        else if (result.status === "created_pending") counts.created += 1;
        else if (result.status === "already_in_track") counts.existing += 1;
        else counts.invalid.push(result.input);
      }
      if (counts.linked + counts.created > 0) {
        onCandidatesAdded();
      }
      // Close right away; the per-domain outcome moves into a notification.
      const parts: string[] = [];
      if (counts.linked)
        parts.push(`${counts.linked} linked to the knowledge graph`);
      if (counts.created)
        parts.push(`${counts.created} added — gathering website data`);
      if (counts.existing) parts.push(`${counts.existing} already in the list`);
      if (counts.invalid.length)
        parts.push(`invalid: ${counts.invalid.join(", ")}`);
      showNotification(
        parts.length ? parts.join(" · ") : "No domains added",
        counts.invalid.length ? "warning" : "success",
      );
      handleClose();
    } catch {
      showNotification("Adding domains failed", "error");
    } finally {
      setSubmittingDomains(false);
    }
  };

  const handleClose = () => {
    // Reset per-open state so the next open starts clean.
    setSearchTerm("");
    setSearchResults([]);
    setAddedIds(new Set());
    setDomainsText("");
    setTab(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      // Fixed height: the dialog must not grow/shrink as results stream in.
      PaperProps={{ sx: { height: 620, maxHeight: "85vh" } }}
    >
      <DialogTitle>Add Candidates</DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Search graph" />
        <Tab label="Add by domain" />
      </Tabs>
      <DialogContent
        // The content pane never scrolls itself — the search field / paste box
        // stay pinned and only the result list scrolls.
        sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {tab === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search organizations by name…"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={18} />
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{ mt: 1, mb: 1 }}
            />
            <List dense sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 0 }}>
              {searchResults.map((org) => {
                const added = addedIds.has(org.id);
                return (
                  <ListItem
                    key={org.id}
                    sx={{ py: 0.5, pr: 12, "& + &": { mt: 0.5 } }}
                    secondaryAction={
                      <Button
                        size="small"
                        variant={added ? "text" : "outlined"}
                        color={added ? "success" : "primary"}
                        disabled={added || addingId === org.id}
                        startIcon={added ? <CheckIcon /> : <AddIcon />}
                        onClick={() => handleAddOrg(org)}
                        sx={{ py: 0, minHeight: 28 }}
                      >
                        {added ? "Added" : "Add"}
                      </Button>
                    }
                  >
                    {/* Two columns: name (flexible, opens profile) | country (fixed) */}
                    <Typography
                      variant="body2"
                      noWrap
                      onClick={() => window.open(`/entity/${org.id}`, "_blank")}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        mr: 1,
                        cursor: "pointer",
                        "&:hover": {
                          textDecoration: "underline",
                          color: "primary.main",
                        },
                      }}
                    >
                      {org.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ width: 130, flexShrink: 0 }}
                    >
                      {org.home_country || "—"}
                    </Typography>
                  </ListItem>
                );
              })}
              {!searching &&
                searchTerm.trim().length >= 2 &&
                searchResults.length === 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ px: 2, py: 1 }}
                  >
                    No organizations found — try the "Add by domain" tab to add
                    a company that is not in the graph yet.
                  </Typography>
                )}
            </List>
          </Box>
        )}
        {tab === 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: 0,
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Paste one domain per line. Domains already in the knowledge graph
              are linked to their organization; unknown domains are added as
              pending candidates — their website is crawled for a profile and
              they are queued for graph ingestion.
            </Typography>
            <TextField
              fullWidth
              multiline
              // Stretch to the dialog actions: the input row fills the tab's
              // remaining height and the textarea scrolls inside it.
              sx={{
                flex: 1,
                minHeight: 0,
                "& .MuiInputBase-root": {
                  height: "100%",
                  alignItems: "flex-start",
                },
                "& .MuiInputBase-input": {
                  height: "100% !important",
                  overflow: "auto !important",
                },
              }}
              // Placeholder only while the field is untouched — it reads as
              // prefilled content in a multiline box, so hide it on focus.
              placeholder={
                domainsFocused ? "" : "acme-defense.com\nhttps://newco.de\n…"
              }
              onFocus={() => setDomainsFocused(true)}
              onBlur={() => setDomainsFocused(false)}
              value={domainsText}
              onChange={(event) => setDomainsText(event.target.value)}
              disabled={submittingDomains}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        {tab === 1 && (
          <Button
            variant="contained"
            onClick={handleSubmitDomains}
            disabled={submittingDomains || domainsText.trim().length === 0}
            startIcon={
              submittingDomains ? <CircularProgress size={16} /> : <AddIcon />
            }
          >
            {submittingDomains ? "Adding…" : "Add domains"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddCandidatesModal;
